/**
 * cloudAlertsApi.js — API functions for the V2 Cloud Alerts page (5.0 platform).
 *
 * fetchCloudMaintenance — polls device maintenance (batteries, comm lost, other)
 *                          via `getdevicemaintenance` stored procedure (60s interval)
 *
 * acceptAlert  — POST /emergency/accept  (write accept datetime + user info to the alarm)
 * finishAlert  — POST /emergency/finish  (write finish datetime + reason to the alarm)
 *
 * Both emergency endpoints expect a `{ "payload": {...} }` wrapper and return
 * `{ Status: "OK" }` on success. On backend update the AppSync subscription
 * pokes all connected clients so the row repaints naturally — no optimistic
 * client mutation needed.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth.js';

// ---------------------------------------------------------------------------
// Base URL helper (mirrors src/services/api.js pattern)
// ---------------------------------------------------------------------------
const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

/**
 * Low-level POST to an /emergency/* endpoint.
 * @param {string} path — route suffix, e.g. "/accept" or "/finish"
 * @param {object} payload — inner payload object (wrapped in `{payload: ...}` by this helper)
 * @returns {Promise<[boolean, string?]>}
 */
async function postEmergency(path, payload) {
    try {
        const url = getBaseUrl() + import.meta.env.VITE_EVENT_E_ENDPOINT + path;
        const resp = await fetch(url, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({ payload }),
        });

        // 500/503/403 won't necessarily parse as JSON — handle them first.
        if (resp.status === 403) return [false, 'Forbidden, user not authorized.'];
        if (resp.status === 500) return [false, 'Internal server error. Please contact support.'];
        if (resp.status === 503) return [false, 'Service unavailable. Please try again.'];

        let data = null;
        try { data = await resp.json(); } catch { /* swallow — treat as unknown */ }

        if (data?.Status === 'OK') return [true];

        const code = data?.ErrorCode ? ` (${data.ErrorCode})` : '';
        return [false, `Request failed${code}.`];
    } catch (err) {
        console.error(`[cloudAlertsApi] postEmergency(${path}) error:`, err);
        return [false, 'Request failed. Please check your connection and try again.'];
    }
}

// ---------------------------------------------------------------------------
// Fetch Cloud Maintenance (batteries + communication lost + other)
// ---------------------------------------------------------------------------

/**
 * Calls `getdevicemaintenance` stored procedure.
 * Returns flat array of maintenance items with snake_case fields:
 *   { event_datetime_utc, condition, device_name, manufacture_uuid,
 *     manufacture_id, device_id, area_name, area_id, resident_name, resident_id }
 *
 * The caller splits by `condition` into batteries / lost / other buckets.
 *
 * @param {number} campusId — numeric campus ID
 * @returns {Promise<Array>}
 */
export function fetchCloudMaintenance(campusId) {
    return apiFetch({
        procedure: 'getdevicemaintenance',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
        readOnly: true,
    });
}

// ---------------------------------------------------------------------------
// Accept an emergency alert
// ---------------------------------------------------------------------------

/**
 * Accept an alert — writes accepted_datetime_utc / accepted_user_id /
 * accepted_username / accepted_display onto the DynamoDB alarm row.
 *
 * Backend validator requires: campus_id, event_id, user_id, display_name, username.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} args.eventId
 * @param {number} args.userId
 * @param {string} args.username
 * @param {string} args.displayName
 * @returns {Promise<[boolean, string?]>}
 */
export function acceptAlert({ campusId, eventId, userId, username, displayName }) {
    return postEmergency('/accept', {
        campus_id: campusId,
        event_id: eventId,
        user_id: userId,
        username: username || '',
        display_name: displayName || username || '',
    });
}

// ---------------------------------------------------------------------------
// Finish an emergency alert
// ---------------------------------------------------------------------------

/**
 * Finish an alert — writes finish_datetime_utc, reason_id, resident_id onto
 * the DynamoDB alarm row.
 *
 * Backend validator requires: campus_id, event_id, reason_id.
 * resident_id is optional on the backend (defaults to 0) but we pass it
 * through when known so the archived row is complete.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} args.eventId
 * @param {number} args.reasonId — cloud_reason_code_id from core_reason_codes
 * @param {number} [args.residentId]
 * @param {number} [args.userId] — acting (logged-in) user. The Lambda compares
 *   this against the alarm's accepted_user_id to enforce "only the accepter may
 *   finish", and makeLogPayload() writes it to the audit trail as contact_info.
 * @returns {Promise<[boolean, string?]>}
 */
export function finishAlert({ campusId, eventId, reasonId, residentId, userId }) {
    return postEmergency('/finish', {
        campus_id: campusId,
        event_id: eventId,
        reason_id: reasonId,
        resident_id: residentId || 0,
        user_id: userId || 0,
    });
}

// ---------------------------------------------------------------------------
// Acknowledge an emergency alert (SafelyYou fall)
// ---------------------------------------------------------------------------

/**
 * Acknowledge a fall alert. Routed through the /transaction Lambda (RequestType
 * `acknowledge_alert`) rather than hitting the SafelyYou `acknowledge_link`
 * directly from the browser — that URL is cross-origin and CORS-blocked. The
 * Lambda makes the server-to-server call. We send the acting user_id and the
 * acknowledge_link; a `Status: "OK"` response → the video is NOT opened (that's
 * the separate "View Video" GET link).
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {number} args.userId — acting (logged-in) user
 * @param {string} args.clientId — session id
 * @param {string} args.acknowledgeLink — the row's `acknowledge_link` URL
 * @returns {Promise<[boolean, string?]>}
 */
export function acknowledgeAlert({ campusId, eventId, userId, clientId, acknowledgeLink }) {
    if (!acknowledgeLink) return Promise.resolve([false, 'No acknowledge link available.']);
    return apiTransaction({
        transType: 'acknowledge_alert',
        payload: [{ user_id: userId, acknowledge_link: acknowledgeLink, campus_id: campusId, event_id: eventId }],
        campusId,
        userId,
        clientId,
    });
}

/**
 * Silently log that a user opened the SafelyYou "View Video" link (audit trail of
 * who took the link). Routed through /transaction (RequestType
 * `acknowledge_alert_link`). Fire-and-forget from the caller — no toast; the
 * caller console-logs any non-OK result.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} args.eventId
 * @param {number} args.userId — acting (logged-in) user
 * @param {string} args.clientId — session id
 * @returns {Promise<[boolean, string?]>}
 */
export function logAcknowledgeLinkTaken({ campusId, eventId, userId, clientId }) {
    if (!eventId) return Promise.resolve([false, 'No event id available.']);
    return apiTransaction({
        transType: 'acknowledge_alert_link',
        payload: [{ user_id: userId, event_id: eventId, campus_id: campusId }],
        campusId,
        userId,
        clientId,
    });
}
